import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function SmsTerms() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="sms-terms-page">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Button asChild variant="ghost" size="sm" className="mb-6">
          <Link to="/login" data-testid="back-to-login">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Link>
        </Button>

        <div className="flex items-center gap-3 mb-2">
          <MessageSquare className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">
            SMS Terms &amp; Conditions
          </h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">
          Effective date: May 6, 2026 &middot; JobBoard by American Piping Inspection
        </p>

        <section className="space-y-6 text-sm leading-6">
          <p>
            These SMS Terms &amp; Conditions ("SMS Terms") govern the SMS
            notification service provided by JobBoard ("we", "us", "our"). By
            providing your mobile phone number to JobBoard, you agree to these
            SMS Terms and to receive text messages from us as described below.
          </p>

          <div>
            <h2 className="text-lg font-semibold mb-2">1. Program Description</h2>
            <p>
              JobBoard sends transactional SMS notifications to employees,
              contractors, and administrators related to their work schedule
              and account. Specifically:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>New job assignment alerts (job title, date, time, location).</li>
              <li>Shift swap approval or rejection notifications.</li>
              <li>Time-off request status updates (approved / rejected).</li>
              <li>Account-related notices initiated by your administrator.</li>
            </ul>
            <p className="mt-2">
              We do not send promotional, marketing, or advertising SMS through
              this program.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">2. How to Opt In</h2>
            <p>
              You opt in by voluntarily providing your mobile phone number to
              JobBoard in one of the following ways:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>
                During account registration on JobBoard, by entering your
                phone number in the optional <strong>Phone</strong> field after
                acknowledging the SMS disclosure shown next to the field.
              </li>
              <li>
                When an administrator creates an employee account on your
                behalf and enters your phone number after you have provided it
                to them.
              </li>
              <li>
                By updating your profile in JobBoard to add a phone number at a
                later date.
              </li>
            </ul>
            <p className="mt-2">
              Providing a phone number is optional. SMS notifications are
              enabled by default for new phone numbers but can be disabled at
              any time (see "How to Opt Out" below).
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">3. Message Frequency</h2>
            <p>
              Message frequency varies based on your individual work schedule,
              assignments, and time-off requests. A typical user may receive
              between 0 and 10 messages per week. There is no fixed schedule.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">4. Message &amp; Data Rates</h2>
            <p>
              Message and data rates may apply to each message sent to or
              received from JobBoard, in accordance with the rate plan you have
              with your mobile carrier. JobBoard is not responsible for any
              charges incurred. Contact your mobile carrier for details on
              messaging plans and rates.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">5. How to Opt Out</h2>
            <p>
              You may stop receiving SMS notifications at any time by either:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>
                Replying <strong>STOP</strong> (or <strong>UNSUBSCRIBE</strong>,{' '}
                <strong>CANCEL</strong>, <strong>END</strong>, or{' '}
                <strong>QUIT</strong>) to any SMS we send. Your number will be
                immediately unsubscribed from the program. You may receive one
                final confirmation message acknowledging your opt-out.
              </li>
              <li>
                Logging in to JobBoard and disabling the SMS toggle under{' '}
                <strong>Profile &rarr; Notification Settings</strong>.
              </li>
            </ul>
            <p className="mt-2">
              To resubscribe after opting out, reply <strong>START</strong> (or{' '}
              <strong>UNSTOP</strong> / <strong>RESUBSCRIBE</strong>) to the
              last message you received, or re-enable SMS in your Notification
              Settings.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">6. Help &amp; Support</h2>
            <p>
              For help, reply <strong>HELP</strong> to any SMS we send, or
              contact us at{' '}
              <a
                className="text-primary hover:underline"
                href="mailto:notify@apijobboard.com"
              >
                notify@apijobboard.com
              </a>
              . You may also contact your JobBoard administrator for assistance.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">7. Supported Carriers</h2>
            <p>
              JobBoard SMS notifications are supported on major U.S. mobile
              carriers, including AT&amp;T, T-Mobile, Verizon Wireless, Sprint,
              U.S. Cellular, Boost Mobile, MetroPCS, Cricket Wireless, and Google
              Fi, among others. Carriers are not liable for delayed or
              undelivered messages.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">8. Privacy</h2>
            <p>
              Your mobile information is handled in accordance with our{' '}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              . As stated there:{' '}
              <strong>
                No mobile information will be shared with third
                parties/affiliates for marketing/promotional purposes; this
                information will not be shared with any third parties.
              </strong>
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">9. Eligibility</h2>
            <p>
              You must be at least 18 years of age or have permission from a
              parent or legal guardian to subscribe to the JobBoard SMS
              program. You confirm that the phone number you provide is your
              own or that you are otherwise authorized to receive messages at
              that number.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">10. Changes to These Terms</h2>
            <p>
              We may revise these SMS Terms from time to time. The "Effective
              date" above will be updated to reflect the latest revision.
              Continued use of the SMS program after a revision constitutes
              acceptance of the updated terms.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">11. Contact</h2>
            <p>
              Questions about these SMS Terms can be sent to{' '}
              <a
                className="text-primary hover:underline"
                href="mailto:notify@apijobboard.com"
              >
                notify@apijobboard.com
              </a>
              .
            </p>
          </div>
        </section>

        <div className="mt-12 pt-6 border-t text-xs text-muted-foreground">
          &copy; {new Date().getFullYear()} American Piping Inspection &middot; JobBoard
        </div>
      </div>
    </div>
  );
}
